/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type ChangeEvent, useCallback, useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Pagination } from '../../components/ui/pagination.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  financeApi,
  type Invoice,
  type InvoicesPageParams,
  type InvoiceStatus,
} from '../../lib/api/finance.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useInvoicesPage, useRecordPayment } from '../../lib/queries/finance.js';
import { uiStore } from '../../lib/stores/ui-store.js';

import { InvoiceModals } from './invoice-modals.js';
import { InvoiceTableRow } from './invoice-row.js';

interface InvoicesViewProps {
  readonly titleKey: string;
  readonly subtitleKey: string;
  /** Admin/Finance manage (create + record payment); Parent is read-only. */
  readonly canManage: boolean;
}

const COLS = ['number', 'client', 'amount', 'issued', 'due', 'status', 'payment'] as const;
const PAGE_LIMIT = 20;
type StatusFilter = 'all' | InvoiceStatus;
const STATUSES: readonly StatusFilter[] = [
  'all',
  'draft',
  'sent',
  'partial',
  'paid',
  'overdue',
  'void',
];

/** Build the server query params from the page's filter state. */
function pageParams(offset: number, q: string, status: StatusFilter): InvoicesPageParams {
  return {
    limit: PAGE_LIMIT,
    offset,
    ...(q !== '' ? { q } : {}),
    ...(status !== 'all' ? { status } : {}),
  };
}

const Toolbar = observer(function Toolbar({
  status,
  onStatus,
}: {
  readonly status: StatusFilter;
  readonly onStatus: (value: StatusFilter) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value);
  }, []);
  const onSelect = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onStatus(e.target.value as StatusFilter);
    },
    [onStatus],
  );
  return (
    <div className="mb-4 flex flex-wrap gap-3">
      <input
        type="search"
        value={uiStore.search}
        onChange={onSearch}
        placeholder={t('finance.invoices.searchPlaceholder')}
        aria-label={t('finance.invoices.searchPlaceholder')}
        className="focus-ring h-11 flex-1 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      />
      <select
        value={status}
        onChange={onSelect}
        aria-label={t('finance.invoices.statusFilter')}
        className="focus-ring h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      >
        {STATUSES.map((value) => (
          <option key={value} value={value}>
            {value === 'all' ? t('finance.invoices.allStatus') : t(`invoiceStatus.${value}`)}
          </option>
        ))}
      </select>
    </div>
  );
});

function InvoicesTable({
  rows,
  clientNames,
  canManage,
  onPay,
  onMarkPaid,
  markingId,
  onSelfPay,
  onCreditNote,
  onReceipt,
}: {
  readonly rows: readonly Invoice[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly canManage: boolean;
  readonly onPay: (invoice: Invoice) => void;
  readonly onMarkPaid: (invoice: Invoice) => void;
  readonly markingId: string | null;
  readonly onSelfPay: (invoice: Invoice) => void;
  readonly onCreditNote: (invoice: Invoice) => void;
  readonly onReceipt: (invoice: Invoice) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          <tr>
            {COLS.map((c) => (
              <th key={c} className="px-5 py-3 font-semibold">
                {t(`finance.invoices.col.${c}`)}
              </th>
            ))}
            <th className="px-5 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((inv) => (
            <InvoiceTableRow
              key={inv.id}
              invoice={inv}
              clientName={clientNames.get(inv.clientId) ?? '—'}
              canPay={canManage}
              onPay={onPay}
              onMarkPaid={onMarkPaid}
              marking={markingId === inv.id}
              canSelfPay={!canManage}
              onSelfPay={onSelfPay}
              onCreditNote={onCreditNote}
              onReceipt={onReceipt}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Invoices table + New-Invoice, Record-Payment and Credit-Note modals (Figma Finance). */
export const InvoicesView = observer(function InvoicesView({
  titleKey,
  subtitleKey,
  canManage,
}: InvoicesViewProps): JSX.Element {
  const { t } = useTranslation();
  const [newOpen, setNewOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  const [payNowInvoice, setPayNowInvoice] = useState<Invoice | null>(null);
  const [creditInvoice, setCreditInvoice] = useState<Invoice | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [offset, setOffset] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState('');
  const search = uiStore.search;
  const invoices = useInvoicesPage(pageParams(offset, debouncedQ, status));
  const clients = useClients();
  const record = useRecordPayment();
  const clientNames = new Map((clients.data ?? []).map((c) => [c.id, c.fullName]));

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(search.trim());
      setOffset(0);
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [search]);

  useEffect(() => {
    if (canManage && searchParams.get('new') === '1') {
      setNewOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [canManage, searchParams, setSearchParams]);

  const openNew = useCallback(() => {
    setNewOpen(true);
  }, []);
  const closeNew = useCallback(() => {
    setNewOpen(false);
  }, []);
  const closePay = useCallback(() => {
    setPayInvoice(null);
  }, []);
  const closePayNow = useCallback(() => {
    setPayNowInvoice(null);
  }, []);
  const closeCredit = useCallback(() => {
    setCreditInvoice(null);
  }, []);
  const onStatus = useCallback((value: StatusFilter) => {
    setStatus(value);
    setOffset(0);
  }, []);
  const onMarkPaid = useCallback(
    (invoice: Invoice) => {
      const balancePaise = invoice.amountPaise - invoice.amountPaidPaise;
      if (balancePaise <= 0) {
        return;
      }
      record.mutate({ id: invoice.id, payload: { amountPaise: balancePaise, method: 'cash' } });
    },
    [record],
  );
  const onReceipt = useCallback((invoice: Invoice) => {
    void financeApi.downloadReceipt(invoice);
  }, []);

  const rows = invoices.data?.items ?? [];
  const total = invoices.data?.total ?? 0;
  const markingId = record.isPending ? record.variables.id : null;

  return (
    <div className="p-8">
      <PageHeader
        title={t(titleKey)}
        subtitle={t(subtitleKey, { count: total })}
        action={
          canManage ? (
            <Button onClick={openNew}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('finance.invoices.add')}
            </Button>
          ) : undefined
        }
      />
      <Toolbar status={status} onStatus={onStatus} />

      <QueryState
        isLoading={invoices.isLoading}
        isError={invoices.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('finance.invoices.empty')}
      >
        <InvoicesTable
          rows={rows}
          clientNames={clientNames}
          canManage={canManage}
          onPay={setPayInvoice}
          onMarkPaid={onMarkPaid}
          markingId={markingId}
          onSelfPay={setPayNowInvoice}
          onCreditNote={setCreditInvoice}
          onReceipt={onReceipt}
        />
        <Pagination total={total} limit={PAGE_LIMIT} offset={offset} onOffsetChange={setOffset} />
      </QueryState>

      <InvoiceModals
        newOpen={newOpen}
        onNewOpenChange={setNewOpen}
        onCloseNew={closeNew}
        payInvoice={payInvoice}
        onClosePay={closePay}
        payNowInvoice={payNowInvoice}
        onClosePayNow={closePayNow}
        creditInvoice={creditInvoice}
        onCloseCredit={closeCredit}
      />
    </div>
  );
});
